import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { Pagination } from '@core/models/pagination.model';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { CurrencyHttpService } from '@core/services/currency-http.service';
import { AppPermissionService } from '@core/services/app-permission.service';
import { of, Subscription } from 'rxjs';
import { exhaustMap, map, tap } from 'rxjs/operators';
import { PaymentModeDataService } from './services/payment-mode-data.service';
import { PaymentModeDialogComponent } from './dialogs/payment-mode-edit.component';
import { PaymentModeImagePreviewComponent } from './dialogs/payment-mode-image-preview.component';
import { PaymentModePositionEditDialogComponent } from './dialogs/payment-mode-position-edit.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'kt-payment-mode',
  templateUrl: './payment-mode.component.html',
  styleUrls: ['./payment-mode.component.scss']
})
export class PaymentModeComponent implements OnInit, OnDestroy {

  form: FormGroup;
  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';
  statuses = [
    "Inactive",
    "Active"
  ];

  dropdown = {
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    perPage: this.dropdownHttpService.perPage,
    types: [
      { id: 3, name: 'All' },
      { id: 1, name: 'Member' },
      { id: 2, name: 'Merchant' },
    ],
    paymentModeStatuses: this.dropdownHttpService.paymentModeStatuses,
    paymentOptions: [],
    paymentProcessors: [],
    locales: []
  };
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  paymentModes$ = [];
  messages$ = this.paymentModeDataService.messages$;
  paymentOptionDropdownSettings = {};
  paymentOptionDropdownList = [];
  paymentOptionSelectedItems: any;
  paymentProcessorDropdownSettings = {};
  paymentProcessorDropdownList = [];
  paymentProcessorSelectedItems: any;

  sortingConfig = {
    'id': 'desc',
    'name': 'desc',
    'status': 'desc',
    'created_at': 'desc',
    'updated_at': 'desc'
  };
  sortingSelection = {
    'sort_by': 'id',
    'sort_order': 'desc',
  };

  // permissions
  canCreatePaymentMode: boolean;
  canUpdatePaymentMode: boolean;
  canEditPaymentModePosition: boolean;

  private subscriptions = new Subscription();
  private subscription = new Subscription();

  constructor(
    private dropdownHttpService: DropdownHttpService,
    private loadingBar: LoadingBarService,
    private currencyHttpService: CurrencyHttpService,
    private appPermissionService: AppPermissionService,
    private paymentModeDataService: PaymentModeDataService,
    public dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.setCurrencyDropdown();
    this.formInit();
    this.paymentOptionSelectedItems = [{ id: 'all', name: 'All' }];
    this.paymentProcessorSelectedItems = [{ id: 'all', name: 'All' }];
    this.paymentOptionDropdownSettings = {
      singleSelection: true,
      text: 'Please Select',
      enableFilterSelectAll: false,
      enableSearchFilter: true,
      classes: 'dropdown',
      // maxHeight: 'auto',
      primaryKey: 'name',
      labelKey: 'name',
      lazyLoading: false,
      noDataLabel: '',
      showCheckbox: false
    };
    this.paymentProcessorDropdownSettings = {
      singleSelection: true,
      text: 'Please Select',
      enableFilterSelectAll: false,
      enableSearchFilter: true,
      classes: 'dropdown',
      // maxHeight: 'auto',
      primaryKey: 'name',
      labelKey: 'name',
      lazyLoading: false,
      noDataLabel: '',
      showCheckbox: false
    };
    this.dropdownHttpService.paymentOptions.subscribe(res => {
      this.dropdown.paymentOptions = res;
      this.paymentOptionDropdownList = [{
        id: 'all',
        name: 'All'
      }, ... this.dropdown.paymentOptions];
    });
    this.dropdownHttpService.paymentProcessor.subscribe(res => {
      this.dropdown.paymentProcessors = res;
      this.paymentProcessorDropdownList = [{
        id: 'all',
        name: 'All'
      }, ... this.dropdown.paymentProcessors];
    });
    this.dropdownHttpService.locales.subscribe(res => {
      this.dropdown.locales = res;

    });
    this.reload();

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canCreatePaymentMode = appPermissions.create_payment_mode;
      this.canUpdatePaymentMode = appPermissions.update_payment_mode;
      this.canEditPaymentModePosition = appPermissions.edit_payment_mode_position;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  private formInit() {
    this.form = new FormGroup({
      settings_currency_id: new FormControl('all'),
      status: new FormControl('all'),
      name: new FormControl(null),
      payment_option_id: new FormControl('all'),
      payment_processor_id: new FormControl('all'),
    });
  }

  private setCurrencyDropdown() {
    if (this.dropdown.currencies.length === 0) {
      // set interval to get currencies from sessionStorage, to prevent returning empty currency dropdown
      if (sessionStorage.getItem('currencies') != undefined && sessionStorage.getItem('currencies') != null && sessionStorage.getItem('currencies') !== '') {
        var interval = setInterval(() => {
          this.dropdown.currencies = JSON.parse(sessionStorage.getItem('currencies'));
          if (this.dropdown.currencies != null) {
            clearInterval(interval);
          }
        }, 100);
      } else {
        this.currencyHttpService.setCurrency().subscribe(res => {
          this.dropdown.currencies = res;
        });
      }
    }
  }

  reload() {
    this.onViewPageBy(this.page);
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = clearSearch ? false : true;
    this.paymentModes$ = [];
    this.loading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        const parameters = this.params ? `&${this.params}` : '';
        this.loadingBar.start();
        return this.paymentModeDataService.getWithQuery(`?perPage=${this.pageSize}${parameters}&${this.generateSortingParam()}`).pipe(
          tap(res => {
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.paymentModes$ = res;
            this.page = 1;
            this.pagination = this.paymentModeDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();
  }

  onClear() {
    this.clearBtnLoading = true;
    this.formInit();
    this.paymentOptionSelectedItems = [{ id: 'all', name: 'All' }];
    this.paymentProcessorSelectedItems = [{ id: 'all', name: 'All' }];
    this.onSubmit(true);
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    this.paymentModes$ = [];
    return this.paymentModeDataService.getWithQuery(`?page=${page}&perPage=${pageSize}${params}&${this.generateSortingParam()}`).pipe(
      tap(res => {
        this.loading = false;
        this.paymentModes$ = res;
        this.pagination = this.paymentModeDataService.pagination;
        this.loadingBar.complete();
      })
    ).subscribe();
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  onOpenPreview(row: any) {
    this.dialog.open(PaymentModeImagePreviewComponent, {
      width: '1000px',
      data: {
        desktopImage: row.desktop_image,
        mobileImage: row.mobile_image
      }
    });
  }

  onChangeStatus(row: any, value: number) {
    if (row.status !== value) {
      const data = {
        id: row.id,
        status: value
      };
      this.subscription = this.paymentModeDataService.updateStatus(data).subscribe(() =>
        this.messages$ = this.paymentModeDataService.messages$
      );
    }
  }

  onOpenDialog(type: string, data?: any) {
    if (type === 'create') {
      this.openDialogBy(PaymentModeDialogComponent, { mode: 'create' });
    } else if (type === 'edit') {
      this.openDialogBy(PaymentModeDialogComponent, { mode: 'edit', paymentMode: data });
    } else if (type === 'edit-position') {
      this.subscription = this.paymentModeDataService.getPaymentModePosition(data.id).pipe(
        tap((res: any) => {
          if (res) {
            this.openDialogBy(PaymentModePositionEditDialogComponent, { mode: 'edit-position', paymentModePosition: res });
          }
        })
      ).subscribe();
    } else {
    }
  }

  private openDialogBy(componentRef: any, data: { mode: string, paymentMode?: any, paymentModePosition?: any }) {
    if (data.mode === 'edit-position') {
      const dialogRef = this.dialog.open(componentRef, {
        width: '800px',
        data: {
          paymentMode: data.paymentModePosition
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.onViewPageBy(this.page);
        }
      });
    } else {
      const dialogRef = this.dialog.open(componentRef, {
        width: '800px',
        data: {
          mode: data.mode,
          paymentMode: data.paymentMode
        }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.onViewPageBy(this.page);
        }
      });
    }
  }

  onSortColumn(property: string) {
    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key == property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }

      }
    }

    // User selection
    this.sortingSelection.sort_by = property;

    if (this.sortingSelection.sort_by === property) {
      // Same column
      this.sortingSelection.sort_order = this.sortingConfig[property];
    } else {
      // Switch to other column
      this.sortingConfig[property] = 'asc';
      this.sortingSelection.sort_order = 'asc';
    }
    // Load Data
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  private generateSortingParam() {
    const sortingParams = Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
    return sortingParams;
  }
}
